import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  clearStripeCheckoutSessionId,
  stashStripeCheckoutSessionId,
} from "../lib/checkoutSessionRecovery.js";
import { syncCheckoutSessionFromStripeWithRetry } from "../lib/stripe.js";
import { useCoachBilling } from "../context/CoachBillingContext.jsx";
import {
  billingErrorCopy,
  isStaleCheckoutSessionError,
  toUserFacingBillingError,
} from "../lib/userFacingErrors.js";

export default function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetchSubscription } = useCoachBilling();

  const [phase, setPhase] = useState("idle"); 
  const [syncError, setSyncError] = useState(null);

  async function runSync(sessionId) {
    const id = sessionId.trim();
    stashStripeCheckoutSessionId(id);
    setPhase("syncing");
    setSyncError(null);
    try {
      await syncCheckoutSessionFromStripeWithRetry(id, { retries: 4, baseDelayMs: 900 });
      clearStripeCheckoutSessionId();
      await refetchSubscription({ silent: true });
      setPhase("ok");
      return true;
    } catch (e) {
      const raw = e?.message ?? String(e);
      if (isStaleCheckoutSessionError(raw)) {
        clearStripeCheckoutSessionId();
      }
      setSyncError(
        toUserFacingBillingError(raw, {
          fallback: billingErrorCopy.couldNotLinkSubscription,
        }),
      );
      setPhase("error");
      return false;
    }
  }

  useEffect(() => {
    let cancelled = false;
    let navTimer;

    (async () => {
      const sessionId = searchParams.get("session_id");

      const redirectTo = searchParams.get("redirect") || "/dashboard";

      if (!sessionId?.trim()) {
        if (!cancelled) setPhase("no_session");
        navTimer = setTimeout(() => {
          if (!cancelled) navigate(redirectTo);
        }, 4000);
        return;
      }

      await runSync(sessionId);
      if (cancelled) return;
      
    })();

    return () => {
      cancelled = true;
      if (navTimer) clearTimeout(navTimer);
    };
    
  }, [navigate, searchParams]);

  const redirectTo = searchParams.get("redirect") || "/dashboard";

  useEffect(() => {
    if (phase !== "ok") return undefined;
    const t = setTimeout(() => navigate(redirectTo), 3500);
    return () => clearTimeout(t);
  }, [phase, navigate, redirectTo]);

  async function handleRetry() {
    const sessionId = searchParams.get("session_id");
    if (!sessionId?.trim()) return;
    await runSync(sessionId);
  }

  const showSpinner = phase === "idle" || phase === "syncing";
  const showError = phase === "error";
  const showOk = phase === "ok";
  const showNoSession = phase === "no_session";

  return (
    <div className="hc-page-shell relative min-h-screen w-full overflow-hidden font-sans">
      <div
        className="pointer-events-none absolute left-1/2 top-[40%] size-[320px] -translate-x-[120%] rounded-full bg-hc-warm/15 blur-[64px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[42%] size-[288px] translate-x-[20%] rounded-full bg-hc-secondary/20 blur-[64px]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[384px] flex-col items-center justify-center px-4 py-16">
        <img src="/logo_full.png" alt="Hey Coach AI logo" className="mb-8 w-full max-w-[384px] h-auto" />

        <div className="w-full rounded-[16px] border border-hc-surface-border-strong bg-hc-surface p-7 shadow-[0px_25px_50px_0px_rgba(0,0,0,0.35)]">
          <div className="flex flex-col items-center gap-5 text-center">
            {showSpinner && (
              <>
                <div className="flex size-14 items-center justify-center rounded-full border border-hc-primary/30 bg-hc-primary/10">
                  <div className="size-7 animate-spin rounded-full border-2 border-hc-primary border-t-transparent" />
                </div>
                <div>
                  <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">
                    Saving your subscription…
                  </h1>
                  <p className="mt-2 text-xs leading-5 text-hc-text-dim">
                    Billing updates don’t always arrive instantly — we’re saving your subscription now (with retries).
                    Don’t close this tab yet.
                  </p>
                </div>
              </>
            )}

            {showOk && (
              <>
                <div className="flex size-14 items-center justify-center rounded-full border border-hc-success/30 bg-hc-success-muted">
                  <i className="icon-check text-2xl leading-none text-hc-success" aria-hidden="true" />
                </div>
                <div>
                  <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">
                    You&apos;re all set!
                  </h1>
                  <p className="mt-2 text-md leading-5 text-hc-text">
                    Your subscription is linked to this account.
                  </p>
                  <p className="mt-2 text-xs leading-5 text-hc-text-dim">
                    {redirectTo === "/dashboard" ? "Opening your dashboard…" : "Taking you back to billing…"}
                  </p>
                </div>
              </>
            )}

            {showError && (
              <>
                <div className="flex size-14 items-center justify-center rounded-full border border-red-500/35 bg-red-500/10">
                  <i className="icon-warning text-2xl leading-none text-red-400" aria-hidden="true" />
                </div>
                <div className="w-full">
                  <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">
                    Couldn&apos;t link your subscription
                  </h1>
                  <p className="mt-2 text-left text-xs leading-5 text-red-400 break-words">
                    {syncError}
                  </p>
                  <p className="mt-3 text-left text-xs leading-5 text-hc-text-dim">
                    Your payment may have gone through — only linking it to this account failed. Try
                    again below, or open your plan page.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRetry()}
                  className="mt-1 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-base font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 active:scale-95"
                >
                  Retry linking subscription
                </button>
              </>
            )}

            {showNoSession && (
              <>
                <div className="flex size-14 items-center justify-center rounded-full border border-hc-surface-border bg-hc-surface">
                  <i className="icon-info text-2xl leading-none text-hc-text-dim" aria-hidden="true" />
                </div>
                <div>
                  <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">
                    Success
                  </h1>
                  <p className="mt-2 text-xs leading-5 text-hc-text-dim">
                    No checkout session id in this URL — taking you to the dashboard.
                  </p>
                </div>
              </>
            )}

            {(showOk || showNoSession) && (
              <button
                type="button"
                onClick={() => navigate(redirectTo)}
                className="mt-1 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-base font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 active:scale-95"
              >
                {redirectTo === "/dashboard" ? "Go to dashboard" : "Back to billing"}
                <i className="icon-arrow-right leading-none" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
